import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { readingBeeAPI, recordingsAPI, studentAPI, narrativeAPI, classroomAPI } from '@/lib/api';
import { BrutalCard, BrutalButton, BrutalBadge } from '@/components/brutal';
import { Award, Trophy, Plus, Play, Pause, Star, ChevronDown, ChevronUp, Users, BookOpen, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import MediaConsentDialog from './MediaConsentDialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const statusColors = { open: 'emerald', voting: 'amber', closed: 'rose' };

const EMOJI_OPTIONS = ['🏆', '🎯', '📚', '⭐', '🎨', '🎉', '🔥', '🌟', '📖', '🎖️', '🐝', '🎭', '✨', '🚀'];

const ReadingBeeTab = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [expandedBee, setExpandedBee] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [audioEl, setAudioEl] = useState(null);

  // editingId is null when creating a new Bee, or the Bee's id when editing.
  const [editingId, setEditingId] = useState(null);

  // Form state. scope = "family" (all my kids) | "custom" (pick kids) | "classroom" (pick classrooms)
  const [form, setForm] = useState({
    title: '', description: '', narrative_id: '',
    prize_description: '', start_date: '', end_date: '',
    scope: 'family',
    target_student_ids: [],
    target_classroom_ids: [],
    cover_emoji: '🏆',
  });

  const toggleTarget = (key, id) => {
    setForm((prev) => {
      const cur = prev[key] || [];
      return { ...prev, [key]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
    });
  };

  const resetForm = () => setForm({
    title: '', description: '', narrative_id: '', prize_description: '',
    start_date: '', end_date: '', scope: 'family',
    target_student_ids: [], target_classroom_ids: [], cover_emoji: '🏆',
  });

  const startEdit = (bee) => {
    setEditingId(bee.id);
    setShowCreate(true);
    setForm({
      title: bee.title || '',
      description: bee.description || '',
      narrative_id: bee.narrative_id || '',
      prize_description: bee.prize_description || '',
      start_date: bee.start_date || '',
      end_date: bee.end_date || '',
      scope: bee.scope === 'guardian' ? 'family' : (bee.scope || 'family'),
      target_student_ids: bee.target_student_ids || [],
      target_classroom_ids: bee.target_classroom_ids || [],
      cover_emoji: bee.cover_emoji || '🏆',
    });
  };

  const { data: bees = [], isLoading } = useQuery({
    queryKey: ['reading-bees', showArchived],
    queryFn: async () => (await readingBeeAPI.getAll({ includeArchived: showArchived })).data,
  });

  const { data: beeDetail } = useQuery({
    queryKey: ['reading-bee-detail', expandedBee],
    queryFn: async () => (await readingBeeAPI.getById(expandedBee)).data,
    enabled: !!expandedBee,
  });

  const { data: students = [] } = useQuery({
    queryKey: ['students-for-bee', user?.id],
    queryFn: async () => (await studentAPI.getAll(user?.id)).data,
    enabled: !!user?.id,
  });

  const { data: classrooms = [] } = useQuery({
    queryKey: ['classrooms-for-bee'],
    queryFn: async () => {
      try { return (await classroomAPI.getAll()).data || []; }
      catch { return []; }
    },
    enabled: showCreate,
  });

  // Fetch narratives for all students to build story picker
  const { data: allNarratives = [] } = useQuery({
    queryKey: ['narratives-for-bee', students.map(s => s.id).join(',')],
    queryFn: async () => {
      const results = [];
      for (const s of students) {
        try {
          const res = await narrativeAPI.getAll(s.id);
          const narrs = res.data || [];
          narrs.forEach(n => results.push({ ...n, student_name: s.full_name }));
        } catch {}
      }
      return results;
    },
    enabled: students.length > 0 && showCreate,
  });

  const { data: recordings } = useQuery({
    queryKey: ['guardian-recordings-for-bee'],
    queryFn: async () => (await recordingsAPI.getGuardianAll()).data,
    enabled: showCreate || !!expandedBee,
  });

  const createMut = useMutation({
    mutationFn: (data) => readingBeeAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['reading-bees']);
      setShowCreate(false);
      setEditingId(null);
      resetForm();
      toast.success('Reading Bee created!');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create'),
  });

  const editMut = useMutation({
    mutationFn: ({ id, data }) => readingBeeAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['reading-bees']);
      queryClient.invalidateQueries(['reading-bee-detail', expandedBee]);
      setShowCreate(false);
      setEditingId(null);
      resetForm();
      toast.success('Reading Bee updated!');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update'),
  });

  const handleSave = () => {
    // Backend requires targets for classroom/custom scopes.
    if (form.scope === 'classroom' && form.target_classroom_ids.length === 0) {
      toast.error('Pick at least one classroom');
      return;
    }
    if (form.scope === 'custom' && form.target_student_ids.length === 0 && form.target_classroom_ids.length === 0) {
      toast.error('Pick at least one child or classroom');
      return;
    }
    if (editingId) {
      editMut.mutate({ id: editingId, data: form });
    } else {
      createMut.mutate(form);
    }
  };

  const [beeConsent, setBeeConsent] = useState(null);
  const submitMut = useMutation({
    mutationFn: ({ beeId, data }) => readingBeeAPI.submitEntry(beeId, data),
    onSuccess: () => { queryClient.invalidateQueries(['reading-bee-detail', expandedBee]); queryClient.invalidateQueries(['reading-bees']); toast.success('Entry submitted!'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to submit'),
  });

  const voteMut = useMutation({
    mutationFn: ({ beeId, data }) => readingBeeAPI.vote(beeId, data),
    onSuccess: () => { queryClient.invalidateQueries(['reading-bee-detail', expandedBee]); toast.success('Vote recorded!'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to vote'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => readingBeeAPI.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries(['reading-bees']); queryClient.invalidateQueries(['reading-bee-detail', expandedBee]); toast.success('Updated!'); },
  });

  const archiveMut = useMutation({
    mutationFn: (id) => readingBeeAPI.archive(id),
    onSuccess: () => { queryClient.invalidateQueries(['reading-bees']); toast.success('Reading Bee archived'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to archive'),
  });

  const unarchiveMut = useMutation({
    mutationFn: (id) => readingBeeAPI.unarchive(id),
    onSuccess: () => { queryClient.invalidateQueries(['reading-bees']); toast.success('Reading Bee restored'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to restore'),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => readingBeeAPI.delete(id),
    onSuccess: () => { queryClient.invalidateQueries(['reading-bees']); toast.success('Reading Bee deleted'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete'),
  });

  const playEntry = async (recordingId) => {
    if (playing === recordingId && audioEl) { audioEl.pause(); setPlaying(null); return; }
    if (audioEl) audioEl.pause();
    let url;
    try { url = await recordingsAPI.streamUrl(recordingId); } catch { toast.error('Recording not available'); return; }
    const a = new Audio(url);
    a.onended = () => setPlaying(null);
    a.onerror = () => { setPlaying(null); toast.error('Recording not available'); };
    a.play().catch(() => setPlaying(null));
    setAudioEl(a);
    setPlaying(recordingId);
  };

  // Filter recordings for the selected bee's story
  const getMatchingRecordings = (narrativeId) => {
    if (!recordings?.recordings) return [];
    return recordings.recordings.filter(r => r.narrative_id === narrativeId);
  };

  if (isLoading) return <div className="text-center py-12 text-2xl font-bold">Loading Reading Bees...</div>;

  return (
    <div className="space-y-6" data-testid="reading-bee-tab">
      {beeConsent && (
        <MediaConsentDialog kind="bee" onCancel={() => setBeeConsent(null)}
          onConfirm={() => { submitMut.mutate(beeConsent); setBeeConsent(null); }} />
      )}
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-black uppercase flex items-center gap-2"><Trophy size={28} className="text-amber-500" /> Reading Bee</h2>
          <p className="text-sm text-gray-500 font-medium">Expressive reading competitions — read, record, and vote!</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowArchived((v) => !v)}
            className={`px-3 py-2 border-4 border-black text-xs font-bold uppercase ${showArchived ? 'bg-amber-200' : 'bg-white hover:bg-amber-100'}`}
            data-testid="toggle-archived-bees"
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
          <BrutalButton
            variant="amber"
            onClick={() => {
              if (showCreate) { setShowCreate(false); setEditingId(null); resetForm(); }
              else { setEditingId(null); resetForm(); setShowCreate(true); }
            }}
            className="flex items-center gap-2"
          >
            <Plus size={16} /> {showCreate ? 'Close' : 'Create Reading Bee'}
          </BrutalButton>
        </div>
      </div>

      {/* Create Form */}
      {showCreate && (
        <BrutalCard shadow="lg" className="bg-amber-50">
          <h3 className="text-xl font-black uppercase mb-4">
            {editingId ? 'Edit Reading Bee' : 'Create a Reading Bee'}
          </h3>

          {/* Emoji picker — sets the cover emoji shown on cards and student banner */}
          <div className="mb-4">
            <label className="text-xs font-bold uppercase text-gray-600 block mb-2">Cover Emoji</label>
            <div className="flex flex-wrap gap-2">
              {EMOJI_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setForm({ ...form, cover_emoji: emoji })}
                  className={`text-2xl w-12 h-12 border-4 border-black flex items-center justify-center ${form.cover_emoji === emoji ? 'bg-amber-300' : 'bg-white hover:bg-amber-100'}`}
                  aria-label={`Pick ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">Title</label>
              <input className="w-full px-3 py-2 border-4 border-black font-bold" placeholder="Spring Reading Bee" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">Select Story</label>
              <select className="w-full px-3 py-2 border-4 border-black font-bold text-sm bg-white" value={form.narrative_id} onChange={e => setForm({ ...form, narrative_id: e.target.value })}>
                <option value="">-- Choose a story --</option>
                {allNarratives.map(n => (
                  <option key={n.id} value={n.id}>{n.title} ({n.student_name})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">Start Date</label>
              <input type="date" className="w-full px-3 py-2 border-4 border-black font-bold" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">End Date</label>
              <input type="date" className="w-full px-3 py-2 border-4 border-black font-bold" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">Prize Description (optional)</label>
              <input className="w-full px-3 py-2 border-4 border-black font-bold" placeholder="e.g. Free audiobook subscription for 1 month" value={form.prize_description} onChange={e => setForm({ ...form, prize_description: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase text-gray-600 block mb-1">Description</label>
              <textarea className="w-full px-3 py-2 border-4 border-black font-bold" rows={2} placeholder="Show us your most expressive reading!" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>

            {/* Scope selector — who sees this Bee? */}
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase text-gray-600 block mb-2">Who is this Reading Bee for?</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { value: 'family', label: 'My whole family', hint: 'All my children' },
                  { value: 'custom', label: 'Specific children', hint: 'Pick who' },
                  { value: 'classroom', label: 'Classroom(s)', hint: 'Pick classes' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm({ ...form, scope: opt.value, target_student_ids: [], target_classroom_ids: [] })}
                    className={`border-4 border-black p-3 text-left ${form.scope === opt.value ? 'bg-amber-300' : 'bg-white hover:bg-amber-100'}`}
                  >
                    <p className="font-black text-sm">{opt.label}</p>
                    <p className="text-xs text-gray-600">{opt.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Student picker — scope=custom */}
            {form.scope === 'custom' && (
              <div className="md:col-span-2">
                <label className="text-xs font-bold uppercase text-gray-600 block mb-2">Pick children</label>
                {students.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No children on this account yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {students.map((s) => {
                      const checked = form.target_student_ids.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => toggleTarget('target_student_ids', s.id)}
                          className={`border-4 border-black px-3 py-1 text-sm font-bold ${checked ? 'bg-emerald-300' : 'bg-white'}`}
                        >
                          {checked ? '✓ ' : ''}{s.full_name || s.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Classroom picker — scope=classroom */}
            {form.scope === 'classroom' && (
              <div className="md:col-span-2">
                <label className="text-xs font-bold uppercase text-gray-600 block mb-2">Pick classroom(s)</label>
                {classrooms.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No classrooms yet. Create one first.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {classrooms.map((c) => {
                      const checked = form.target_classroom_ids.includes(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleTarget('target_classroom_ids', c.id)}
                          className={`border-4 border-black px-3 py-1 text-sm font-bold ${checked ? 'bg-emerald-300' : 'bg-white'}`}
                        >
                          {checked ? '✓ ' : ''}{c.name || c.session_code || c.id.slice(0, 8)}
                          {c.participating_students?.length ? ` (${c.participating_students.length})` : ''}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <BrutalButton variant="emerald" onClick={handleSave} disabled={!form.title || !form.narrative_id || !form.start_date || !form.end_date}>
              {editingId ? 'Save Changes' : 'Create Reading Bee'}
            </BrutalButton>
            {editingId && (
              <BrutalButton
                variant="ghost"
                onClick={() => { setEditingId(null); setShowCreate(false); resetForm(); }}
              >
                Cancel
              </BrutalButton>
            )}
          </div>
        </BrutalCard>
      )}

      {/* Bees List */}
      {bees.length === 0 ? (
        <BrutalCard shadow="xl" className="text-center py-12">
          <Trophy size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-2xl font-bold mb-2">No Reading Bees Yet</p>
          <p className="text-lg text-gray-500">Create one or wait for a platform contest to be announced!</p>
        </BrutalCard>
      ) : (
        <div className="space-y-4">
          {bees.map(bee => (
            <BrutalCard key={bee.id} shadow="lg">
              {/* Bee Header */}
              <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setExpandedBee(expandedBee === bee.id ? null : bee.id)}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-2xl" aria-hidden>{bee.cover_emoji || '🏆'}</span>
                    <h3 className="text-lg font-black">{bee.title}</h3>
                    <BrutalBadge variant={statusColors[bee.status] || 'indigo'} size="sm">{bee.status}</BrutalBadge>
                    <BrutalBadge variant="indigo" size="sm">{bee.scope}</BrutalBadge>
                    {bee.archived && (
                      <BrutalBadge variant="ghost" size="sm">archived</BrutalBadge>
                    )}
                    {bee.winner_entry_id && bee.status === 'closed' && (
                      <BrutalBadge variant="emerald" size="sm">🏅 winner</BrutalBadge>
                    )}
                    {bee.created_by === user?.id && (
                      <div className="ml-auto flex gap-1 flex-wrap">
                        <button
                          onClick={(e) => { e.stopPropagation(); startEdit(bee); }}
                          className="text-xs font-bold uppercase px-2 py-1 border-2 border-black bg-white hover:bg-amber-200"
                          aria-label="Edit"
                        >
                          Edit
                        </button>
                        {bee.archived ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); unarchiveMut.mutate(bee.id); }}
                            className="text-xs font-bold uppercase px-2 py-1 border-2 border-black bg-white hover:bg-emerald-200"
                            aria-label="Restore"
                          >
                            Restore
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); archiveMut.mutate(bee.id); }}
                            className="text-xs font-bold uppercase px-2 py-1 border-2 border-black bg-white hover:bg-gray-200"
                            aria-label="Archive"
                          >
                            Archive
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${bee.title}"? This cannot be undone.`)) {
                              deleteMut.mutate(bee.id);
                            }
                          }}
                          className="text-xs font-bold uppercase px-2 py-1 border-2 border-black bg-white hover:bg-rose-200 text-rose-600"
                          aria-label="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    <BookOpen size={12} className="inline mr-1" />{bee.narrative_title}
                    {' · '}<Users size={12} className="inline mr-1" />{bee.entry_count} entries
                    {bee.prize_description && <>{' · '}<Award size={12} className="inline mr-1" />Prize: {bee.prize_description}</>}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    <Clock size={10} className="inline mr-1" />{bee.start_date} — {bee.end_date}
                    {bee.creator_name && ` · By ${bee.creator_name}`}
                  </p>
                </div>
                <div className="flex-shrink-0 mt-1">
                  {expandedBee === bee.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </div>

              {/* Expanded Detail */}
              {expandedBee === bee.id && beeDetail && (
                <div className="mt-4 pt-4 border-t-4 border-black space-y-4">
                  {bee.description && <p className="text-sm text-gray-600 italic">{bee.description}</p>}

                  {/* Winner reveal for closed bees — preserved even after
                      contest ends so parents can play it back any time. */}
                  {beeDetail.status === 'closed' && beeDetail.winner_entry_id && (() => {
                    const winner = (beeDetail.entries || []).find((e) => e.id === beeDetail.winner_entry_id);
                    if (!winner) return null;
                    return (
                      <div className="bg-amber-100 border-4 border-amber-500 p-4">
                        <h4 className="font-black uppercase text-sm mb-2 flex items-center gap-2">
                          <Trophy size={18} className="text-amber-600" /> Winner
                        </h4>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => playEntry(winner.recording_id)}
                            className="w-10 h-10 rounded-full bg-amber-600 text-white flex items-center justify-center"
                            aria-label="Play winner recording"
                          >
                            {playing === winner.recording_id ? <Pause size={14} /> : <Play size={14} />}
                          </button>
                          <div>
                            <p className="font-black">{winner.student_name}{winner.student_age ? ` (age ${winner.student_age})` : ''}</p>
                            <p className="text-xs text-gray-600">
                              Ch. {winner.chapter_number} · avg {winner.average_score || '—'} from {winner.vote_count || 0} votes
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Submit Entry (if open) */}
                  {beeDetail.status === 'open' && !beeDetail.has_entered && (
                    <div className="bg-emerald-50 border-4 border-black p-4">
                      <h4 className="font-black text-sm uppercase mb-2">Submit Your Entry</h4>
                      <p className="text-xs text-gray-500 mb-3">Select a recording of "{beeDetail.narrative_title}" to enter:</p>
                      {getMatchingRecordings(beeDetail.narrative_id).length > 0 ? (
                        <div className="space-y-2">
                          {getMatchingRecordings(beeDetail.narrative_id).map(rec => (
                            <div key={rec.id} className="flex items-center justify-between bg-white border-2 border-black p-3">
                              <div className="flex items-center gap-3">
                                <button onClick={(e) => { e.stopPropagation(); playEntry(rec.id); }} className="w-8 h-8 rounded-full bg-indigo-500 text-white flex items-center justify-center">
                                  {playing === rec.id ? <Pause size={12} /> : <Play size={12} />}
                                </button>
                                <div>
                                  <p className="text-sm font-bold">{rec.student_name} — Ch. {rec.chapter_number}</p>
                                  <p className="text-xs text-gray-400">{new Date(rec.created_date).toLocaleDateString()}</p>
                                </div>
                              </div>
                              <BrutalButton variant="emerald" size="sm" onClick={() => setBeeConsent({
                                beeId: beeDetail.id,
                                data: { recording_id: rec.id, student_name: rec.student_name, student_age: rec.student_age, consent: true }
                              })}>
                                Enter
                              </BrutalButton>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No recordings found for this story. Record a chapter first!</p>
                      )}
                    </div>
                  )}

                  {beeDetail.has_entered && beeDetail.status === 'open' && (
                    <div className="bg-indigo-50 border-2 border-indigo-300 p-3 text-center">
                      <p className="text-sm font-bold text-indigo-600">You've already entered this Reading Bee!</p>
                    </div>
                  )}

                  {/* Entries + Voting */}
                  {beeDetail.entries?.length > 0 && (
                    <div>
                      <h4 className="font-black text-sm uppercase mb-2">Entries ({beeDetail.entries.length})</h4>
                      <div className="space-y-2">
                        {beeDetail.entries.map((entry, idx) => (
                          <div key={entry.id} className="flex items-center justify-between bg-white border-2 border-black p-3">
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-black text-gray-300 w-6">#{idx + 1}</span>
                              <button onClick={() => playEntry(entry.recording_id)} className="w-8 h-8 rounded-full bg-indigo-500 text-white flex items-center justify-center flex-shrink-0">
                                {playing === entry.recording_id ? <Pause size={12} /> : <Play size={12} />}
                              </button>
                              <div>
                                <p className="text-sm font-bold">{entry.student_name}{entry.student_age ? ` (age ${entry.student_age})` : ''}</p>
                                <p className="text-xs text-gray-400">Ch. {entry.chapter_number} · {entry.vote_count} votes · Avg: {entry.average_score || '—'}</p>
                              </div>
                            </div>
                            {/* Vote stars */}
                            {(beeDetail.status === 'voting' || beeDetail.status === 'open') && (
                              <div className="flex items-center gap-1">
                                {[1,2,3,4,5].map(s => (
                                  <button key={s} onClick={() => voteMut.mutate({ beeId: beeDetail.id, data: { entry_id: entry.id, score: s } })} className="p-0.5">
                                    <Star size={18} className={s <= (beeDetail.user_votes?.[entry.id] || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-300 hover:text-amber-300'} />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status controls for creator */}
                  {beeDetail.created_by === user?.id && (
                    <div className="flex gap-2 pt-2">
                      {beeDetail.status === 'open' && (
                        <BrutalButton variant="amber" size="sm" onClick={() => updateMut.mutate({ id: beeDetail.id, data: { status: 'voting' } })}>
                          Start Voting
                        </BrutalButton>
                      )}
                      {beeDetail.status === 'voting' && (
                        <BrutalButton variant="rose" size="sm" onClick={() => updateMut.mutate({ id: beeDetail.id, data: { status: 'closed' } })}>
                          Close & Announce Winner
                        </BrutalButton>
                      )}
                    </div>
                  )}
                </div>
              )}
            </BrutalCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReadingBeeTab;
